import { useEffect, useMemo, useState } from 'react';
import {
  Box, Typography, Alert, TextField, CircularProgress, InputAdornment, Chip,
  Stack, Paper, Tabs, Tab, Divider, Tooltip, ToggleButton, ToggleButtonGroup,
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';
import { getRederiReference } from '../api/featureMap';

// REDERI 플랫폼 소개/홍보 + 탐색 허브.
// 데이터: /api/rederi/reference (rayrndsw/rederi 의 agent/skill/command/module 라이브).
// AI 물어보기는 우측 상단 전역 "REDERI에게 물어보기" (RederiAssistant) 로 이동.

const TIER_META = {
  core: { label: 'core', color: '#3fb950', desc: '자동 설치 — 모든 REDERI repo 기본 탑재' },
  recommended: { label: 'recommended', color: '#d29922', desc: '권장 — --enable 로 활성화' },
  'opt-in': { label: 'opt-in', color: '#8b949e', desc: '선택 — 필요 시 --enable 로 활성화' },
};

// QMS(의료기기 IEC 62304 컴플라이언스) 자동화 분류 — 모듈 설명 앞 "Tier A/B1/B2/B3".
// tier(설치 강도)와는 별개 축: A=차단 게이트(예방), B=자동화 도구(집계/운영/전달).
const QMS_TIER = {
  A:  { label: 'QMS · A', full: '품질 게이트', color: '#f85149', desc: 'PR 시점에 위반을 차단 (예방)' },
  B1: { label: 'QMS · B1', full: '집계 · 대시보드', color: '#58a6ff', desc: '산출물 인덱스 갱신 · 문서 신뢰도(Doc Health)' },
  B2: { label: 'QMS · B2', full: '운영 자동화', color: '#3fb950', desc: '이슈 상태 전이 · 분기 종합 감사' },
  B3: { label: 'QMS · B3', full: '외부 전달', color: '#d29922', desc: '핵심 산출물 PDF · docs 패키지 빌드' },
};
const qmsOf = (desc) => ((desc || '').match(/^Tier\s+(A|B1|B2|B3)\b/) || [])[1] || null;
const stripQms = (desc) => (desc || '').replace(/^Tier\s+(?:A|B1|B2|B3)\s*[—–-]\s*/, '');

// Agent group(workflow stage) 한국어 라벨 — 없으면 원문 그대로.
const GROUP_LABEL = {
  requirements: '요구사항 · 기획',
  spec: '스펙 · 설계',
  dev: '개발 · 구현',
  test: '테스트',
  review: '리뷰',
  deploy: '배포 · 릴리즈',
  release: '배포 · 릴리즈',
  monitoring: '모니터링',
  docs: '문서화',
  doc: '문서화',
  security: '보안',
  poc: 'PoC · 검증',
  meet: '회의',
  Web: '플랫폼 — Web',
  Android: '플랫폼 — Android',
  iOS: '플랫폼 — iOS',
  UX: '플랫폼 — UX',
};

// 코드 호스팅(vcs)별 설치·적용 경로. Bitbucket 은 plugin 정본(private GitHub)에 접근할 수
// 없어 읽기전용 미러에서 받고, 신규 repo 생성 자동화(/rederi-create)가 GitHub 전용이라
// 신규·기존이 모두 /rederi-adopt 로 수렴한다.
const VCS_GUIDE = {
  github: {
    label: 'GitHub',
    marketplace: '/plugin marketplace add rayrndsw/rederi',
    adoption: [
      { id: 'rederi-create', step: '1', title: '신규 제품 부트스트랩', fallback: 'zero-base 신규 제품 repo 생성' },
      { id: 'rederi-init', step: '2', title: '워크플로우 설치', fallback: '기존 repo 에 SDD·릴리즈·모듈 설치' },
      { id: 'rederi-update', step: '3', title: '플랫폼 갱신', fallback: '.claude 갱신 + 신규 모듈 적용' },
      { id: 'rederi-doctor', step: '4', title: '자가 진단', fallback: 'PC 셋업·설정 점검 및 교정' },
    ],
  },
  bitbucket: {
    label: 'Bitbucket',
    marketplace: '/plugin marketplace add git@bitbucket.org:rayrnd/rederi.git',
    marketplaceNote:
      'GitHub 계정이 없으면 정본(private) 접근이 막히므로 읽기전용 미러에서 받습니다. 백그라운드 자동 갱신이 credential helper 를 끄기 때문에 SSH 를 권장합니다. 미러에는 push·PR 하지 마세요 — main 이 force push 로 덮어써집니다.',
    adoption: [
      { id: 'rederi-adopt', step: '1', title: '하네스 적용 — 신규·기존 공통', arg: '--preset=bitbucket-jira', fallback: 'repo 스캔 → 환경 자동추론 → 워크플로우 설치' },
      { id: 'rederi-update', step: '2', title: '플랫폼 갱신', fallback: '.claude 갱신 + 신규 모듈 적용' },
      { id: 'rederi-doctor', step: '3', title: '자가 진단', fallback: 'PC 셋업·설정 점검 및 교정' },
    ],
  },
};

const norm = (s) => (s || '').toLowerCase();

export default function GuidePage() {
  const [ref, setRef] = useState({ agents: [], skills: [], modules: [], commands: [], generatedAt: null });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [tab, setTab] = useState(0);
  const [search, setSearch] = useState('');
  const [vcs, setVcs] = useState('github');
  const guide = VCS_GUIDE[vcs];

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const data = await getRederiReference();
        if (!cancelled) setRef(data);
      } catch (e) {
        if (!cancelled) setError(e.message || String(e));
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  const cmdById = useMemo(() => {
    const map = {};
    ref.commands.forEach((c) => { map[c.id] = c; });
    return map;
  }, [ref.commands]);

  const q = norm(search);
  const fAgents = useMemo(() => ref.agents.filter((a) => !q || norm(`${a.id} ${a.group} ${a.role}`).includes(q)), [ref.agents, q]);
  // group(단계)별 묶기 — 처음 등장한 순서 유지 (워크플로우 단계 순서 → 플랫폼).
  const agentGroups = useMemo(() => {
    const order = [];
    const map = new Map();
    for (const a of fAgents) {
      if (!map.has(a.group)) { map.set(a.group, []); order.push(a.group); }
      map.get(a.group).push(a);
    }
    return order.map((g) => ({ group: g, items: map.get(g) }));
  }, [fAgents]);
  const fSkills = useMemo(() => ref.skills.filter((s) => !q || norm(`${s.id} ${s.category} ${s.desc} ${s.agents}`).includes(q)), [ref.skills, q]);
  const fCommands = useMemo(() => ref.commands.filter((c) => !q || norm(`${c.id} ${c.title} ${c.purpose}`).includes(q)), [ref.commands, q]);
  const fModules = useMemo(() => ref.modules.filter((m) => !q || norm(`${m.id} ${m.tier} ${m.desc}`).includes(q)), [ref.modules, q]);

  return (
    <Box>
      <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 1 }}>
        <AutoAwesomeIcon sx={{ color: 'secondary.main' }} />
        <Typography variant="h2">REDERI 가이드</Typography>
      </Stack>
      <Typography variant="body2" sx={{ color: 'text.secondary', mb: 2, maxWidth: '70ch' }}>
        REDERI 는 AI Agent·Skill·Command 와 SDD(Spec 기반 개발)·릴리즈 자동화 워크플로우로
        개발 업무를 표준화하는 사내 플랫폼입니다. 아래에서 사용법·적용 방법을 보고, 어떤 자동화가
        가능한지 검색할 수 있습니다.
        {ref.agents.length > 0 && (
          <> {' · '}현재 <b>Agent {ref.agents.length}</b> · <b>Skill {ref.skills.length}</b> · <b>Command {ref.commands.length}</b> · <b>모듈 {ref.modules.length}</b></>
        )}
      </Typography>

      <Alert severity="info" icon={<AutoAwesomeIcon fontSize="inherit" />} sx={{ mb: 3 }}>
        무엇이 가능한지 궁금하면 우측 상단 <b>REDERI에게 물어보기</b> 를 눌러 대화형으로 물어보세요 —
        기능뿐 아니라 진행 중인 프로젝트·제품 현황도 답합니다.
      </Alert>

      {loading && <CircularProgress size={28} />}
      {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>{error}</Alert>}

      {!loading && !error && (
        <>
          {/* ── 코드 호스팅 선택 — 설치·적용 경로가 갈린다 ── */}
          <Stack direction="row" alignItems="center" spacing={1.5} sx={{ mb: 2, flexWrap: 'wrap' }}>
            <Typography variant="body2" sx={{ fontWeight: 600 }}>코드를 어디에 두나요?</Typography>
            <ToggleButtonGroup
              size="small"
              exclusive
              value={vcs}
              onChange={(_, v) => v && setVcs(v)}
              aria-label="코드 호스팅 선택"
            >
              {Object.entries(VCS_GUIDE).map(([k, g]) => (
                <ToggleButton key={k} value={k} sx={{ borderRadius: 0, px: 2, textTransform: 'none' }}>
                  {g.label}
                </ToggleButton>
              ))}
            </ToggleButtonGroup>
            <Typography variant="caption" sx={{ color: 'text.secondary' }}>
              설치 방법과 시작 명령이 달라집니다.
            </Typography>
          </Stack>

          {/* ── 설치 (마켓플레이스) ── */}
          <Typography variant="h4" sx={{ mb: 1 }}>설치</Typography>
          <Typography variant="caption" sx={{ color: 'text.secondary', display: 'block', mb: 1.5 }}>
            먼저 Claude Code 에서 REDERI 플러그인을 마켓플레이스에서 설치합니다. 최초 1회만 하면 됩니다.
          </Typography>
          <Paper variant="outlined" sx={{ borderRadius: 0, p: 1.5, mb: 4 }}>
            <Stack spacing={1}>
              <Box>
                <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 0.25 }}>
                  <Chip label="1" size="small" sx={{ bgcolor: 'primary.main', color: '#0d1117', fontWeight: 700, minWidth: 28 }} />
                  <Typography variant="body2" sx={{ fontWeight: 600 }}>마켓플레이스 등록</Typography>
                </Stack>
                <Typography variant="body2" sx={{ fontFamily: 'JetBrains Mono, monospace', color: 'primary.light', pl: 4.5, wordBreak: 'break-all' }}>
                  {guide.marketplace}
                </Typography>
                {guide.marketplaceNote && (
                  <Typography variant="caption" sx={{ color: 'text.secondary', display: 'block', pl: 4.5, mt: 0.5 }}>
                    {guide.marketplaceNote}
                  </Typography>
                )}
              </Box>
              <Box>
                <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 0.25 }}>
                  <Chip label="2" size="small" sx={{ bgcolor: 'primary.main', color: '#0d1117', fontWeight: 700, minWidth: 28 }} />
                  <Typography variant="body2" sx={{ fontWeight: 600 }}>플러그인 설치</Typography>
                </Stack>
                <Typography variant="body2" sx={{ fontFamily: 'JetBrains Mono, monospace', color: 'primary.light', pl: 4.5 }}>
                  /plugin install rederi@rederi --scope project
                </Typography>
              </Box>
              <Typography variant="caption" sx={{ color: 'text.secondary', pl: 4.5 }}>
                설치 후 <b>Claude Code 를 다시 시작</b>하면 아래 <b>적용 방법</b>의 <code>/rederi-*</code> 명령을 쓸 수 있습니다.
                {' '}(<code>--scope project</code> 는 이 repo 에만 적용 — 개인 전체에 적용하려면 생략)
              </Typography>
            </Stack>
          </Paper>

          {/* ── 적용 방법 (트리오) ── */}
          <Typography variant="h4" sx={{ mb: 1 }}>적용 방법</Typography>
          <Typography variant="caption" sx={{ color: 'text.secondary', display: 'block', mb: 1.5 }}>
            플러그인 설치 후, Claude Code 에서 아래 명령을 순서대로 실행하면 REDERI 워크플로우가 repo 에 설치됩니다.
          </Typography>
          <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr', md: `repeat(${guide.adoption.length}, 1fr)` }, gap: 1.5, mb: 2 }}>
            {guide.adoption.map((a) => (
              <Paper key={a.id} variant="outlined" sx={{ borderRadius: 0, p: 1.5, display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                <Stack direction="row" alignItems="center" spacing={1}>
                  <Chip label={a.step} size="small" sx={{ bgcolor: 'primary.main', color: '#0d1117', fontWeight: 700, minWidth: 28 }} />
                  <Typography variant="caption" sx={{ fontFamily: 'JetBrains Mono, monospace', color: 'primary.light', fontWeight: 600 }}>/{a.id}</Typography>
                </Stack>
                {a.arg && (
                  <Typography variant="caption" sx={{ fontFamily: 'JetBrains Mono, monospace', color: 'text.secondary', wordBreak: 'break-all' }}>
                    {a.arg}
                  </Typography>
                )}
                <Typography variant="body2" sx={{ fontWeight: 600 }}>{a.title}</Typography>
                <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                  {(cmdById[a.id]?.purpose) || a.fallback}
                </Typography>
              </Paper>
            ))}
          </Box>

          {vcs === 'bitbucket' && (
            <Alert severity="warning" sx={{ mb: 4 }}>
              <Typography variant="body2" sx={{ fontWeight: 600, mb: 0.5 }}>
                Bitbucket 에서는 신규 제품도 <code>/rederi-adopt</code> 로 시작합니다
              </Typography>
              <Typography variant="caption" sx={{ display: 'block', mb: 0.5 }}>
                <code>/rederi-create</code> 는 원격 저장소 생성이 GitHub 전용이라 쓸 수 없습니다.
                신규 제품은 <b>Bitbucket 에서 repo 를 먼저 만들고</b>(<code>prd-&lt;제품코드&gt;</code>) 클론한 뒤
                <code> /rederi-adopt --preset=bitbucket-jira</code> 를 실행하세요. 빈 repo 여도 동작하며,
                제품 메타가 없으면 제품명·설명·PM 을 묻는 미니 인터뷰가 이어집니다.
                이때 <code>mani</code> 워크스페이스 등록과 <code>README.md</code>·<code>.gitignore</code> 시드는 수동입니다.
              </Typography>
              <Typography variant="caption" sx={{ display: 'block', mb: 0.5 }}>
                프리셋: <code>bitbucket-jira</code>(Jira + Confluence) / <code>bitbucket-local</code>(저장소 마크다운만, 외부 의존 0).
                환경을 잘못 잡았어도 프리셋을 다시 주면 바뀐 항목을 보고하며 교정됩니다.
              </Typography>
              <Typography variant="caption" sx={{ display: 'block' }}>
                <b>아직 수동인 것</b> — PR 생성·리뷰 자동화, 릴리즈(GitHub Release)·Milestone 동기화,
                GitHub Actions 기반 정기 스캔. <code>/rederi-doctor</code> 가 이 범위를 명시적으로 표시합니다.
              </Typography>
            </Alert>
          )}

          {/* ── 카탈로그 탐색 ── */}
          <Typography variant="h4" sx={{ mb: 1 }}>카탈로그</Typography>
          <TextField
            size="small"
            placeholder="검색: 이름 / 설명 / 그룹..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            sx={{ width: { xs: '100%', md: 400 }, mb: 1.5 }}
            InputProps={{ startAdornment: <InputAdornment position="start"><SearchIcon fontSize="small" /></InputAdornment> }}
          />
          <Tabs value={tab} onChange={(_, v) => setTab(v)} sx={{ mb: 2, borderBottom: '1px solid', borderColor: 'divider' }}>
            <Tab label={`Agent (${fAgents.length})`} />
            <Tab label={`Skill (${fSkills.length})`} />
            <Tab label={`Command (${fCommands.length})`} />
            <Tab label={`모듈 (${fModules.length})`} />
          </Tabs>

          {tab === 0 && (
            <Stack spacing={2.5}>
              {agentGroups.map(({ group, items }) => (
                <Box key={group}>
                  <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 0.75 }}>
                    <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>{GROUP_LABEL[group] || group}</Typography>
                    <Chip label={items.length} size="small" variant="outlined" sx={{ height: 18, fontSize: 10 }} />
                  </Stack>
                  <Stack spacing={0.75}>
                    {items.map((a) => (
                      <Paper key={a.id} variant="outlined" sx={{ borderRadius: 0, px: 1.5, py: 1, display: 'flex', alignItems: 'baseline', gap: 1.5, flexWrap: 'wrap' }}>
                        <Typography variant="body2" sx={{ fontWeight: 600, fontFamily: 'JetBrains Mono, monospace', minWidth: 180 }}>{a.id}</Typography>
                        <Typography variant="caption" sx={{ color: 'text.secondary', flexGrow: 1 }}>{a.role}</Typography>
                      </Paper>
                    ))}
                  </Stack>
                </Box>
              ))}
              {fAgents.length === 0 && <Typography variant="caption" sx={{ color: 'text.secondary' }}>검색 결과 없음</Typography>}
            </Stack>
          )}

          {tab === 1 && (
            <Stack spacing={0.75}>
              {fSkills.map((s) => (
                <Paper key={s.id} variant="outlined" sx={{ borderRadius: 0, px: 1.5, py: 1, display: 'flex', alignItems: 'baseline', gap: 1.5, flexWrap: 'wrap' }}>
                  <Typography variant="body2" sx={{ fontWeight: 600, fontFamily: 'JetBrains Mono, monospace', minWidth: 200 }}>{s.id}</Typography>
                  {s.category && <Chip label={s.category} size="small" variant="outlined" sx={{ height: 18, fontSize: 10 }} />}
                  <Typography variant="caption" sx={{ color: 'text.secondary', flexGrow: 1 }}>{s.desc}</Typography>
                </Paper>
              ))}
              {fSkills.length === 0 && <Typography variant="caption" sx={{ color: 'text.secondary' }}>검색 결과 없음</Typography>}
            </Stack>
          )}

          {tab === 2 && (
            <Stack spacing={0.75}>
              {fCommands.map((c) => (
                <Paper key={c.id} variant="outlined" sx={{ borderRadius: 0, px: 1.5, py: 1 }}>
                  <Typography variant="body2" sx={{ fontWeight: 600, fontFamily: 'JetBrains Mono, monospace', color: 'primary.light' }}>/{c.id}</Typography>
                  <Typography variant="caption" sx={{ color: 'text.secondary' }}>{c.purpose || c.title}</Typography>
                </Paper>
              ))}
              {fCommands.length === 0 && <Typography variant="caption" sx={{ color: 'text.secondary' }}>검색 결과 없음</Typography>}
            </Stack>
          )}

          {tab === 3 && (
            <>
              {/* 설치 강도(tier) 범례 */}
              <Typography variant="caption" sx={{ color: 'text.secondary', display: 'block', mb: 0.5 }}>설치 강도</Typography>
              <Stack direction="row" spacing={2} sx={{ mb: 1.5, flexWrap: 'wrap' }}>
                {Object.entries(TIER_META).map(([k, m]) => (
                  <Box key={k} sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                    <Box sx={{ width: 12, height: 12, bgcolor: m.color }} />
                    <Typography variant="caption" sx={{ color: 'text.secondary' }}><b>{m.label}</b> — {m.desc}</Typography>
                  </Box>
                ))}
              </Stack>
              {/* QMS 분류 범례 — 해당 모듈이 있을 때만 */}
              {fModules.some((m) => qmsOf(m.desc)) && (
                <>
                  <Typography variant="caption" sx={{ color: 'text.secondary', display: 'block', mb: 0.5 }}>
                    QMS 자동화 분류 <Typography component="span" variant="caption" sx={{ color: 'text.disabled' }}>(의료기기 IEC 62304 — 설치 강도와 별개 축)</Typography>
                  </Typography>
                  <Stack spacing={0.5} sx={{ mb: 2 }}>
                    {Object.entries(QMS_TIER).map(([k, m]) => (
                      <Box key={k} sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
                        <Chip label={m.label} size="small" sx={{ bgcolor: m.color, color: '#0d1117', fontWeight: 700, height: 18, fontSize: 10 }} />
                        <Typography variant="caption" sx={{ color: 'text.secondary' }}><b>{m.full}</b> — {m.desc}</Typography>
                      </Box>
                    ))}
                  </Stack>
                </>
              )}
              <Stack spacing={0.75}>
                {fModules.map((m) => {
                  const meta = TIER_META[m.tier] || { color: '#8b949e', label: m.tier };
                  const qms = qmsOf(m.desc);
                  const qmsMeta = qms ? QMS_TIER[qms] : null;
                  return (
                    <Paper key={m.id} variant="outlined" sx={{ borderRadius: 0, px: 1.5, py: 1, display: 'flex', alignItems: 'baseline', gap: 1.5, flexWrap: 'wrap' }}>
                      <Chip label={meta.label} size="small" sx={{ bgcolor: meta.color, color: '#0d1117', fontWeight: 600, height: 20 }} />
                      {qmsMeta && (
                        <Tooltip title={`${qmsMeta.full} — ${qmsMeta.desc}`} arrow>
                          <Chip label={qmsMeta.label} size="small" sx={{ bgcolor: qmsMeta.color, color: '#0d1117', fontWeight: 700, height: 20 }} />
                        </Tooltip>
                      )}
                      <Typography variant="body2" sx={{ fontWeight: 600, fontFamily: 'JetBrains Mono, monospace', minWidth: 180 }}>{m.id}</Typography>
                      {m.status && <Chip label={m.status} size="small" variant="outlined" color="warning" sx={{ height: 18, fontSize: 10 }} />}
                      <Typography variant="caption" sx={{ color: 'text.secondary', flexGrow: 1 }}>{stripQms(m.desc)}</Typography>
                    </Paper>
                  );
                })}
                {fModules.length === 0 && <Typography variant="caption" sx={{ color: 'text.secondary' }}>검색 결과 없음</Typography>}
              </Stack>
            </>
          )}

          <Divider sx={{ my: 3 }} />
          <Typography variant="caption" sx={{ color: 'text.secondary' }}>
            REDERI 플랫폼의 기능 카탈로그는 실시간으로 최신 상태를 반영합니다.
            {ref.generatedAt && <> · 업데이트: {new Date(ref.generatedAt).toLocaleString('ko-KR', { hour12: false })}</>}
          </Typography>
        </>
      )}
    </Box>
  );
}
